import { inject, Injectable, signal, DestroyRef } from '@angular/core';
import { UserProfile, UserRole } from '../models/user.model';
import { firstValueFrom } from 'rxjs';
import { LoggerService } from './logger.service';
import { ApiAuthService } from './api-auth.service';
import { ApiUsersService } from './api-users.service';
import { CartService } from './cart.service';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private destroyRef = inject(DestroyRef);
  private logger = inject(LoggerService);
  private apiAuthService = inject(ApiAuthService);
  private apiUsersService = inject(ApiUsersService);
  private cartService = inject(CartService);
  private readonly ROLE_CACHE_KEY = 'cartco_user_role';
  private readonly PROFILE_CACHE_KEY = 'cartco_user_profile';

  profile = signal<UserProfile | null>(null);
  private tokenRefreshInterval: ReturnType<typeof setInterval> | null = null;

  constructor() {
    // Initialize JWT authentication
    this.initializeAuth();

    // Set up token refresh interval (refresh once per day; access token lasts 7 days)
    this.setupTokenRefresh();
  }

  private saveProfileToCache(profile: UserProfile | null): void {
    try {
      if (profile) {
        localStorage.setItem(this.PROFILE_CACHE_KEY, JSON.stringify(profile));
        if (profile.role) {
          localStorage.setItem(this.ROLE_CACHE_KEY, profile.role);
        }
      } else {
        this.clearProfileCache();
      }
    } catch (error) {
      // Ignore cache errors (e.g., private browsing mode)
    }
  }

  private clearProfileCache(): void {
    try {
      localStorage.removeItem(this.PROFILE_CACHE_KEY);
      localStorage.removeItem(this.ROLE_CACHE_KEY);
    } catch (error) {
      // Ignore cache errors
    }
  }

  private restoreProfileFromCache(): void {
    try {
      const cached = localStorage.getItem(this.PROFILE_CACHE_KEY);
      if (cached) {
        const profile = JSON.parse(cached) as UserProfile;
        if (profile.id && profile.role) {
          this.profile.set(profile);
          return;
        }
      }
    } catch (error) {
      // Ignore cache errors
    }
  }

  /**
   * Public method to restore profile from cache (used by guards when API is offline)
   */
  restoreProfileFromCacheIfNeeded(): boolean {
    const currentProfile = this.profile();
    // If profile already exists with a role, no need to restore
    if (currentProfile?.role) {
      return true;
    }
    // Try to restore from cache
    this.restoreProfileFromCache();
    return !!this.profile()?.role;
  }

  /**
   * Load user profile from REST API when using JWT authentication
   */
  private async initializeAuth(): Promise<void> {
    // First, try to restore from cache (fast, works offline)
    this.restoreProfileFromCache();

    // Then, try to restore session from refresh token
    const refreshToken = this.apiAuthService.getRefreshToken();
    
    if (refreshToken) {
      try {
        // Validate refresh token is not expired
        if (this.isTokenValid(refreshToken)) {
          // Try to refresh access token
          // try {
          //   await firstValueFrom(this.apiAuthService.refreshToken());
          //   await this.loadJWTProfile();
          //   this.logger.info('Session restored from refresh token');
          //   return;
          // } catch (error: any) {
          //   // Refresh failed - check if it's expected (401 with not found/revoked)
          //   const isExpectedError = error?.status === 401 && 
          //     error?.error?.message?.includes('not found or revoked');
            
          //   if (isExpectedError) {
          //     // Expected error - token expired/revoked, silently clear
          //     this.logger.debug('Refresh token expired or revoked, clearing tokens');
          //   } else {
          //     // Unexpected error - log as warning
          //     this.logger.warn('Token refresh failed, keeping cached profile', error);
          //   }
            
          //   // Only clear tokens if cached profile is invalid
          //   if (!this.profile()?.id) {
          //     this.apiAuthService.clearTokens();
          //   }
          // }
        } else {
          // Refresh token expired
          this.logger.debug('Refresh token expired');
          
          // Only clear tokens if cached profile is invalid
          if (!this.profile()?.id) {
            this.apiAuthService.clearTokens();
          }
        }
      } catch (error) {
        this.logger.debug('Error validating refresh token', error);
        
        // Only clear tokens if cached profile is invalid
        if (!this.profile()?.id) {
          this.apiAuthService.clearTokens();
        }
      }
    }
    
    // Fallback: If still have access token, try to load profile
    if (this.apiAuthService.isAuthenticated() && !this.profile()) {
      await this.loadJWTProfile();
    }
  }

  /**
   * Load user profile from backend API
   */
  async loadJWTProfile(): Promise<void> {
    if (!this.apiAuthService.isAuthenticated()) {
      return;
    }

    try {
      const previousUserId = this.profile()?.id ?? null;
      const jwtUser = this.apiAuthService.getUser();
      if (jwtUser) {
        // Convert JWT user to UserProfile
        const userProfile = this.apiAuthService.convertToUserProfile(jwtUser);
        
        // Try to fetch full profile from API
        try {
          const fullProfile = await firstValueFrom(this.apiUsersService.getCurrentUser());
          this.profile.set(fullProfile);
          this.saveProfileToCache(fullProfile);
        } catch (error) {
          // If API call fails, use JWT user data
          this.profile.set(userProfile);
          this.saveProfileToCache(userProfile);
          this.logger.warn('Failed to fetch full profile, using JWT data', error);
        }

        // If user changed (account switch), clear cart to avoid leaking another account's items.
        const currentUserId = this.profile()?.id ?? null;
        if (previousUserId && currentUserId && previousUserId !== currentUserId) {
          this.cartService.clear();
        }
      }
    } catch (error) {
      this.logger.warn('Failed to load JWT profile', error);
    }
  }

  /**
   * Public API to refresh user profile from backend (e.g. for pull-to-refresh on Account page).
   */
  async refreshProfile(): Promise<void> {
    await this.loadJWTProfile();
  }

  private isTokenValid(token: string): boolean {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const exp = payload.exp * 1000; // Convert to milliseconds
      // Check if token expires in more than 30 seconds (reduced buffer for better user experience)
      return Date.now() < exp - 30000;
    } catch {
      return false;
    }
  }

  /**
   * Set up automatic token refresh
   */
  private setupTokenRefresh(): void {
    // Clear existing interval if any
    if (this.tokenRefreshInterval) {
      clearInterval(this.tokenRefreshInterval);
      this.tokenRefreshInterval = null;
    }

    // Refresh token every 14 minutes (tokens expire in 15 minutes)
    this.tokenRefreshInterval = setInterval(async () => {
      try {
        if (this.apiAuthService.isAuthenticated()) {
          const refreshToken = this.apiAuthService.getRefreshToken();
          if (refreshToken && this.isTokenValid(refreshToken)) {
            await firstValueFrom(this.apiAuthService.refreshToken());
            this.logger.debug('Token refreshed successfully');
          } else {
            // Refresh token expired, clear interval
            if (this.tokenRefreshInterval) {
              clearInterval(this.tokenRefreshInterval);
              this.tokenRefreshInterval = null;
            }
          }
        } else {
          // Not authenticated, clear interval
          if (this.tokenRefreshInterval) {
            clearInterval(this.tokenRefreshInterval);
            this.tokenRefreshInterval = null;
          }
        }
      } catch (error) {
        this.logger.error('Token refresh failed', error);
        // If token refresh fails, clear interval
        if (this.tokenRefreshInterval) {
          clearInterval(this.tokenRefreshInterval);
          this.tokenRefreshInterval = null;
        }
      }
    }, 24 * 60 * 60 * 1000); // Every 24 hours
  }

  async signOut(): Promise<void> {
    this.clearProfileCache();
    this.cartService.clear();
    
    // Revoke refresh token and clear JWT tokens
    if (this.apiAuthService.getRefreshToken()) {
      try {
        await firstValueFrom(this.apiAuthService.logout());
      } catch (error) {
        // Even if logout request fails, clear local tokens
        this.logger.warn('Logout request failed, clearing local tokens', error);
        this.apiAuthService.clearTokens();
      }
    } else if (this.apiAuthService.isAuthenticated()) {
      this.apiAuthService.clearTokens();
    }
    
    // Clear token refresh interval
    if (this.tokenRefreshInterval) {
      clearInterval(this.tokenRefreshInterval);
      this.tokenRefreshInterval = null;
    }
    
    this.profile.set(null);
  }

  /**
   * Update user profile information (name, email, phone)
   */
  async updateProfile(updates: Partial<Pick<UserProfile, 'name' | 'email' | 'phone'>>): Promise<void> {
    if (!this.apiAuthService.isAuthenticated()) {
      throw new Error('User must be logged in to update profile.');
    }

    const currentProfile = this.profile();
    if (!currentProfile) {
      throw new Error('User profile not found.');
    }

    // Check for duplicate phone number if phone is being updated
    if (updates.phone && updates.phone !== currentProfile.phone) {
      try {
        const phoneCheck = await firstValueFrom(
          this.apiUsersService.checkUserExistsByPhone(updates.phone)
        );
        if (phoneCheck.exists) {
          // Verify it's not the current user
          const existingUser = await firstValueFrom(
            this.apiUsersService.findByPhone(updates.phone)
          );
          if (existingUser && existingUser.id !== currentProfile.id) {
            throw new Error('This phone number is already registered to another account.');
          }
        }
      } catch (error: any) {
        if (error?.message?.includes('already registered')) {
          throw error;
        }
        this.logger.warn('Error checking phone number', error);
      }
    }

    // Check for duplicate email if email is being updated
    if (updates.email && updates.email !== currentProfile.email) {
      // This should be handled by the backend API
      // For now, we'll let the backend validate
    }

    // Update profile via API
    try {
      const updatedProfile = await firstValueFrom(
        this.apiUsersService.updateUser(currentProfile.id, updates)
      );
      this.profile.set(updatedProfile);
      this.saveProfileToCache(updatedProfile);
    } catch (error) {
      this.logger.error('Failed to update profile', error);
      throw error;
    }
  }

  /**
   * Get current user ID token (for API calls if needed)
   */
  async getIdToken(): Promise<string | null> {
    return this.apiAuthService.getToken();
  }
}
